import ExcelJS from 'exceljs';

import {
  cities,
  countries
} from '@features/admin/components/location-details/cityMapping';
import { PaymentCollectionOptions } from '@features/group-management/constants';

const createExcelWithDropdowns = async (
  headers: string[],
  data: string[],
  minAmount: number,
  maxAmount: number
) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Template');
  const validationSheet = workbook.addWorksheet('Validation');

  validationSheet.state = 'hidden';

  worksheet.addRow(headers);
  worksheet.addRow(data);

  const countryColIndex = headers.indexOf('country') + 1;
  const stateColIndex = headers.indexOf('state') + 1;
  const frequencyColIndex = headers.indexOf('frequency') + 1;
  const monthsPerTermColIndex = headers.indexOf('monthsPerTerm') + 1;

  let lastValidationRow = 0;

  if (countryColIndex > 0 && stateColIndex > 0) {
    validationSheet.addRow(['Countries']);
    countries.forEach((country) => validationSheet.addRow([country]));
    validationSheet.addRow(['Others']);

    const countryLastRow = countries.length + 2;
    lastValidationRow = countryLastRow;

    validationSheet.addRow(['States']);
    Object.keys(cities).forEach((state) => validationSheet.addRow([state]));

    const stateStartRow = countryLastRow + 2;
    const stateLastRow = stateStartRow + Object.keys(cities).length - 1;
    lastValidationRow = stateLastRow;

    const stateColumnLetter = worksheet.getColumn(stateColIndex).letter;
    const countryColumnLetter = worksheet.getColumn(countryColIndex).letter;
    worksheet.dataValidations.add(
      `${countryColumnLetter}2:${countryColumnLetter}101`,
      {
        type: 'list',
        formulae: [`Validation!$A$2:$A$${countryLastRow}`],
        showErrorMessage: true,
        errorStyle: 'error',
        errorTitle: 'Invalid Country',
        error: 'Please select a country from the list',
        allowBlank: true
      }
    );

    worksheet.dataValidations.add(
      `${stateColumnLetter}2:${stateColumnLetter}101`,
      {
        type: 'list',
        formulae: [`Validation!$A$${stateStartRow}:$A$${stateLastRow}`],
        showErrorMessage: true,
        errorStyle: 'error',
        errorTitle: 'Invalid State',
        error: 'Please select a state from the list',
        allowBlank: true
      }
    );
  }

  if (frequencyColIndex > 0) {
    validationSheet.addRow(['Frequency']);
    const options = PaymentCollectionOptions.filter(
      (option: { label: string }) => option.label !== 'Once'
    );
    options.forEach((option: { label: string }) =>
      validationSheet.addRow([option.label])
    );

    const frequencyStartRow = lastValidationRow + 2;
    const frequencyLastRow = frequencyStartRow + options.length - 1;
    const frequencyColumnLetter = worksheet.getColumn(frequencyColIndex).letter;

    worksheet.dataValidations.add(
      `${frequencyColumnLetter}2:${frequencyColumnLetter}101`,
      {
        type: 'list',
        formulae: [`Validation!$A$${frequencyStartRow}:$A$${frequencyLastRow}`],
        showErrorMessage: true,
        errorStyle: 'error',
        errorTitle: 'Invalid Frequency',
        error: 'Please select a valid frequency from the list',
        allowBlank: true
      }
    );
  }

  if (monthsPerTermColIndex > 0) {
    const monthsPerTermColumnLetter = worksheet.getColumn(
      monthsPerTermColIndex
    ).letter;
    worksheet.dataValidations.add(
      `${monthsPerTermColumnLetter}2:${monthsPerTermColumnLetter}101`,
      {
        type: 'whole',
        operator: 'between',
        formulae: [2, 12],
        showErrorMessage: true,
        errorStyle: 'error',
        errorTitle: 'Invalid Months',
        error: 'Please enter a number between 2 and 12',
        allowBlank: true
      }
    );
  }

  headers.forEach((header, index) => {
    if (header.startsWith('Term') && !header.endsWith('_duedate')) {
      const termColumnLetter = worksheet.getColumn(index + 1).letter;
      worksheet.dataValidations.add(
        `${termColumnLetter}2:${termColumnLetter}101`,
        {
          type: 'custom',
          formulae: [
            `=OR(${termColumnLetter}2=0, AND(${termColumnLetter}2>=${minAmount}, ${termColumnLetter}2<=${maxAmount}))`
          ],
          showErrorMessage: true,
          errorStyle: 'error',
          errorTitle: 'Invalid Term Value',
          error: `Please enter either 0 or a value between ${minAmount} and ${maxAmount}`,
          allowBlank: true
        }
      );
    }
  });

  worksheet.columns = headers.map(() => ({ width: 20 }));

  const headerRow = worksheet.getRow(1);
  headerRow.font = { bold: true };
  headerRow.alignment = { vertical: 'middle', horizontal: 'center' };

  worksheet.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: headers.length }
  };

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  const url = window.URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = 'template.xlsx';
  link.click();
  window.URL.revokeObjectURL(url);
};

export const createCouponTemplateWithDropdowns = async (
  headers: string[],
  data: string[]
) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Template');
  const validationSheet = workbook.addWorksheet('Validation');

  validationSheet.state = 'hidden';

  worksheet.addRow(headers);
  worksheet.addRow(data);

  const couponTypeColIndex = headers.indexOf('couponType') + 1;

  if (couponTypeColIndex > 0) {
    let lastValidationRow = 0;
    validationSheet.addRow(['Coupon Type']);
    const options = ['percentage', 'amount'];
    options.forEach((option) => validationSheet.addRow([option]));

    const couponTypeStartRow = options.length + 1;
    lastValidationRow = couponTypeStartRow;

    const couponTypeColumnLetter =
      worksheet.getColumn(couponTypeColIndex).letter;

    worksheet.dataValidations.add(
      `${couponTypeColumnLetter}2:${couponTypeColumnLetter}101`,
      {
        type: 'list',
        formulae: [`Validation!$A$2:$A$${lastValidationRow}`],
        showErrorMessage: true,
        errorStyle: 'error',
        errorTitle: 'Invalid Coupon Type',
        error: 'Please select a coupon type from the list',
        allowBlank: false
      }
    );
  }

  // add validation for showDuringRegistration
  const showDuringRegistrationColIndex =
    headers.indexOf('showDuringRegistration') + 1;

  if (showDuringRegistrationColIndex > 0) {
    // Start after the couponType validation rows (which ended at row 3)
    const showDuringRegistrationStartRow = 4; // Start at row 4
    validationSheet.addRow(['Show During Registration']);
    const options = ['Yes', 'No'];
    options.forEach((option) => validationSheet.addRow([option]));

    const showDuringRegistrationLastRow =
      showDuringRegistrationStartRow + options.length;

    const showDuringRegistrationColumnLetter = worksheet.getColumn(
      showDuringRegistrationColIndex
    ).letter;

    worksheet.dataValidations.add(
      `${showDuringRegistrationColumnLetter}2:${showDuringRegistrationColumnLetter}101`,
      {
        type: 'list',
        formulae: [
          `Validation!$A$${
            showDuringRegistrationStartRow + 1
          }:$A$${showDuringRegistrationLastRow}`
        ],
        showErrorMessage: true,
        errorStyle: 'error',
        errorTitle: 'Invalid Show During Registration',
        error: 'Please select Yes or No',
        allowBlank: false
      }
    );
  }

  worksheet.columns = headers.map(() => ({ width: 20 }));

  const headerRow = worksheet.getRow(1);
  headerRow.font = { bold: true };
  headerRow.alignment = { vertical: 'middle', horizontal: 'center' };

  worksheet.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: headers.length }
  };

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  const url = window.URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = 'template.xlsx';
  link.click();
  window.URL.revokeObjectURL(url);
};

export default createExcelWithDropdowns;
